import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/main/authSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import PageHeader from "../../components/PageHeader";
import Spinner from "../../components/Spinner";
import Alert from "../../components/Alert";
import CryptoJS from "crypto-js";
import Toastify from 'toastify-js';
import "toastify-js/src/toastify.css";

const CustomerProfile = () => {
  const user = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  const accountId = accountData?._id;
  const token = localStorage.getItem('installamelo-auth-token');
  
  // Stati per la gestione del profilo
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profile, setProfile] = useState(null);
  
  // Stati per l'editing
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  
  // Stati per il cambio password
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    repeatPassword: ''
  });
  const [passwordStatus, setPasswordStatus] = useState(''); // 'l' = loading, 's' = success, 'f' = failure
  const [passwordError, setPasswordError] = useState('');
  
  useEffect(() => {
    document.title = "Profilo | Installamelo";
    if (user?.customerUserId) {
      loadCustomerProfile();
    }
  }, [user]);
  
  // Carica i dati del profilo
  const loadCustomerProfile = async () => {
    if (!user?.customerUserId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/customers/contact/${user.customerUserId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Errore nel caricamento dei dati del profilo');
      }
      
      const data = await response.json();
      console.log('Dati profilo caricati:', data);
      
      setProfile(data);
      // Prepara i dati per l'editing
      setFormData({
        name: data.name || '',
        surname: data.surname || '',
        email: data.email || '',
        mobile: data.mobile || '',
        role: data.role || '',
        notes: data.notes || ''
      });
    } catch (error) {
      console.error("Errore nel caricamento del profilo:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Gestisce l'input dell'utente nei campi del form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Gestisce l'input dell'utente nei campi della password
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
    setPasswordError(''); // Resetta gli errori quando l'utente inizia a digitare
  };
  
  // Invia i dati del profilo aggiornati
  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const customerId = user.customerId;
      const contactId = user.customerUserId;
      
      if (!customerId || !contactId) {
        throw new Error('Dati utente non disponibili');
      }
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/customers/updateContact`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          },
          body: JSON.stringify({
            accountId,
            customerId,
            contactId,
            contact: formData
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Errore durante l\'aggiornamento del profilo');
      }
      
      // Aggiorna i dati locali
      setProfile({ ...profile, ...formData });
      setSuccess('Profilo aggiornato con successo!');
      setIsEditing(false);
      
      // Mostra toast di conferma
      Toastify({
        text: "Profilo aggiornato con successo",
        duration: 3000,
        close: true,
        gravity: "top",
        position: "right",
        backgroundColor: "#4caf50",
      }).showToast();
    } catch (error) {
      console.error("Errore nell'aggiornamento del profilo:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Gestisce il cambio password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Verifica che le due password siano uguali
    if (passwordData.password !== passwordData.repeatPassword) {
      setPasswordError("Le due password non coincidono");
      setPasswordStatus('f');
      return;
    }
    
    // Verifica che la password non sia vuota
    if (!passwordData.password) {
      setPasswordError("La password non può essere vuota");
      setPasswordStatus('f');
      return;
    }
    
    // Se le password coincidono, procedi con il salvataggio
    setPasswordStatus('l');
    setPasswordError('');
    
    try {
      // Crittografa la password prima di inviarla
      const encryptedPassword = CryptoJS.AES.encrypt(
        passwordData.password,
        import.meta.env.VITE_ENCKEY
      ).toString();
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/customers/changeContactPassword`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          },
          body: JSON.stringify({
            customerId: user.customerId,
            contactId: user.customerUserId,
            password: encryptedPassword
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Errore durante il cambio password");
      }
      
      // Se l'operazione va a buon fine
      setPasswordStatus('s');
      setPasswordData({ password: '', repeatPassword: '' });
      
      // Mostra toast di conferma
      Toastify({
        text: "Password modificata con successo",
        duration: 3000,
        close: true,
        gravity: "top",
        position: "right",
        backgroundColor: "#4caf50",
      }).showToast();
      
      // Dopo un breve delay, nascondi il form della password
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordStatus('');
      }, 2000);
    } catch (error) {
      console.error("Errore durante il cambio password:", error);
      setPasswordError(error.message);
      setPasswordStatus('f');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader title="Il Tuo Profilo" />
      
      {error && (
        <Alert 
          variant="danger" 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}
      
      {success && (
        <Alert 
          variant="success" 
          message={success} 
          onClose={() => setSuccess(null)} 
        />
      )}
      
      {loading && !profile ? (
        <div className="flex justify-center my-8">
          <Spinner size="lg" />
        </div>
      ) : !profile ? (
        <div className="bg-white shadow-md rounded-lg p-6 text-center">
          <p className="text-gray-600">Impossibile caricare i dati del profilo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pannello profilo */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">
                  {isEditing ? "Modifica i tuoi dati" : "Informazioni Personali"}
                </h2>
              </div>
              
              {isEditing ? (
                /* Form di modifica */
                <form onSubmit={handleSubmitProfile} className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                        Nome
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="surname">
                        Cognome
                      </label>
                      <input
                        type="text"
                        id="surname"
                        name="surname"
                        value={formData.surname}
                        onChange={handleInputChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mobile">
                      Telefono
                    </label>
                    <input
                      type="text"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">
                      Ruolo
                    </label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="notes">
                      Note
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      disabled={loading}
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      disabled={loading}
                    >
                      {loading ? "Salvataggio..." : "Salva modifiche"}
                    </button>
                  </div>
                </form>
              ) : (
                /* Visualizzazione dati */
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-4">
                        <label className="block text-gray-500 text-sm mb-1">Nome</label>
                        <div className="font-medium">{profile.name || "N/D"}</div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-gray-500 text-sm mb-1">Cognome</label>
                        <div className="font-medium">{profile.surname || "N/D"}</div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-gray-500 text-sm mb-1">Email</label>
                        <div className="font-medium">{profile.email || "N/D"}</div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="mb-4">
                        <label className="block text-gray-500 text-sm mb-1">Telefono</label>
                        <div className="font-medium">{profile.mobile || "N/D"}</div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-gray-500 text-sm mb-1">Ruolo</label>
                        <div className="font-medium">{profile.role || "N/D"}</div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-gray-500 text-sm mb-1">Note</label>
                        <div className="font-medium whitespace-pre-line">{profile.notes || "Nessuna nota"}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2"
                    >
                      Modifica Profilo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Pannello cambio password */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">Password</h2>
              </div>
              
              <div className="p-6">
                {showPasswordForm ? (
                  <form onSubmit={handleChangePassword}>
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                        Nuova Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={passwordData.password}
                        onChange={handlePasswordChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      />
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="repeatPassword">
                        Conferma Password
                      </label>
                      <input
                        type="password"
                        id="repeatPassword"
                        name="repeatPassword"
                        value={passwordData.repeatPassword}
                        onChange={handlePasswordChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      />
                    </div>
                    
                    {passwordError && (
                      <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
                        {passwordError}
                      </div>
                    )}
                    
                    {passwordStatus === 's' && (
                      <div className="mb-4 p-2 bg-green-100 text-green-700 rounded">
                        Password aggiornata con successo!
                      </div>
                    )}
                    
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordData({ password: '', repeatPassword: '' });
                          setPasswordError('');
                        }}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        disabled={passwordStatus === 'l'}
                      >
                        Annulla
                      </button>
                      <button
                        type="submit"
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        disabled={passwordStatus === 'l' || passwordStatus === 's'}
                      >
                        {passwordStatus === 'l' ? "Salvataggio..." : "Cambia Password"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-6">
                      Per cambiare la password del tuo account, clicca sul pulsante qui sotto.
                    </p>
                    <button
                      onClick={() => setShowPasswordForm(true)}
                      className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                      Cambia Password
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;
